import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { takeUntil, finalize } from 'rxjs/operators';

import { VehiclesFilesService } from '../../../_services/vehicles-files.service';
import { ToasterErrorService } from '../../../_services/toaster.service';

@Component({
    moduleId: module.id,
    selector: 'investigation-app',
    templateUrl: './investigation.component.html',
    styleUrls: ['./investigation.component.scss']
})
export class InvestigationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    
    toasterconfig: any = this.toasterErrorService.toasterconfig;
    investigationForm: FormGroup;
    investigationData: any = null;
    isLoading: boolean = false;
    isSaving: boolean = false;
    investigationId: number;
    activityId: number;
    
    // Form arrays for dynamic questions
    get answersArray(): FormArray {
        return this.investigationForm.get('answers') as FormArray;
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private vehicleFileService: VehiclesFilesService,
        private toasterErrorService: ToasterErrorService
    ) {
        this.createForm();
    }

    ngOnInit() {
        // Get activityId and investigationId from route params
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            this.activityId = +params['activityId'];
            this.investigationId = +params['investigationId'];
            if (this.activityId && this.investigationId) {
                this.loadInvestigationDetails();
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    createForm() {
        this.investigationForm = this.fb.group({
            answers: this.fb.array([]),
            signoff_signature: ['']
        });
    }

    loadInvestigationDetails() {
        this.isLoading = true;
        
        if (this.activityId && this.investigationId) {
            this.vehicleFileService.getInvestigationDetails(this.activityId, this.investigationId)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => this.isLoading = false)
                )
                .subscribe(
                    (data) => {
                        this.investigationData = data;
                        this.populateForm(data);
                    },
                    (error) => {
                        console.error('Error loading investigation details:', error);
                        this.toasterErrorService.toasterErr('Failed to load investigation details');
                    }
                );
        } else {
            this.isLoading = false;
            this.toasterErrorService.toasterErr('Missing activity ID or investigation ID');
        }
    }

    populateForm(data: any) {
        // Set signoff signature form control (only editable field)
        this.investigationForm.get('signoff_signature').setValue(data.signoff_signature || '');

        // Clear existing answers array
        while (this.answersArray.length !== 0) {
            this.answersArray.removeAt(0);
        }

        // Populate answers array - only for questions that exist in the template
        if (data.template && data.template.questions && data.template.questions.length > 0) {
            data.template.questions.forEach(question => {
                // Find existing answer for this question (answers have question object with id)
                const existingAnswer = data.answers ? data.answers.find(a => a.question && a.question.id === question.id) : null;
                
                if (existingAnswer) {
                    // Format date answers for HTML date input
                    let formattedAnswer = existingAnswer.answer;
                    if (question.answer_type === 'DATE' && existingAnswer.answer) {
                        formattedAnswer = this.formatDateForInput(existingAnswer.answer);
                    }
                    
                    // Add existing answer with proper structure
                    this.addAnswer({
                        id: existingAnswer.id,
                        question: question.id,
                        answer: formattedAnswer,
                        attachment: existingAnswer.attachment
                    }, data.status === 'COMPLETE' ? true : false);
                } else {
                    // Create empty answer for this question
                    this.addAnswer({ 
                        question: question.id, 
                        answer: '', 
                        attachment: null 
                    }, data.status === 'COMPLETE' ? true : false);
                }
            });
        }
    }

    addAnswer(answerData: any = {}, disabledInput: boolean) {
        // Get question type from template questions
        const questionType = this.getQuestionTypeById(answerData.question);
        
        // Handle DROPDOWN_AND_TEXT type specially
        if (questionType === 'DROPDOWN_AND_TEXT') {
            const dropdownAnswer = answerData.answer ? this.extractDropdownFromAnswer(answerData.answer) : '';
            const textAnswer = answerData.answer ? this.extractTextFromAnswer(answerData.answer) : '';
            
            const answerGroup = this.fb.group({
                id: [answerData.id || null],
                question_id: [answerData.question || answerData.question_id || null, Validators.required],
                dropdown_answer: [{value: dropdownAnswer, disabled:  disabledInput }, Validators.required],
                text_answer: [{value: textAnswer || '', disabled: disabledInput }, Validators.required],
                attachment: [answerData.attachment || null],
                remove: [false]
            });
            
            this.answersArray.push(answerGroup);
        } else {
            const answerGroup = this.fb.group({
                id: [answerData.id || null],
                question_id: [answerData.question || answerData.question_id || null, Validators.required],
                answer: [{value: answerData.answer || '', disabled: disabledInput }, this.getAnswerValidators(questionType)],
                attachment: [answerData.attachment || null],
                remove: [false]
            });
            
            this.answersArray.push(answerGroup);
        }
    }

    getAnswerValidators(questionType: string) {
        const validators = [];
        
        // Add required validator for most question types
        if (questionType !== 'FILE') {
            validators.push(Validators.required);
        }
        
        // Add specific validators based on question type
        switch (questionType) {
            case 'TEXT':
                validators.push(Validators.maxLength(255));
                break;
            case 'DATE':
                // Date validation can be added here
                break;
            case 'CHOICE':
                // Choice validation can be added here
                break;
        }
        
        return validators;
    }

    onFileSelected(event: any, answerIndex: number) {
        const file = event.target.files[0];
        if (file) {
            // Convert file to base64
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result as string;
                this.answersArray.at(answerIndex).patchValue({
                    attachment: base64
                });
            };
            reader.readAsDataURL(file);
        }
    }

    onSignatureChange(signatureData: string) {
        this.investigationForm.patchValue({
            signoff_signature: signatureData
        });
    }

    saveInvestigation() {
        if (this.investigationForm.valid) {
            this.isSaving = true;
            
            const formData = this.investigationForm.value;
            
            // Prepare answers data
            const answers = formData.answers.map(answer => {
                // Get question type to handle different answer types
                const questionType = this.getQuestionTypeById(answer.question_id);
                let formattedAnswer = answer.answer;
                
                // Handle DROPDOWN_AND_TEXT type
                if (questionType === 'DROPDOWN_AND_TEXT') {
                    formattedAnswer = this.combineDropdownAndText(answer.dropdown_answer, answer.text_answer);
                }
                // Convert HTML date format back to ISO format for API
                else if (questionType === 'DATE' && answer.answer) {
                    formattedAnswer = this.formatDateForAPI(answer.answer);
                }
                
                const answerData: any = {
                    answer: formattedAnswer
                };
                
                if (answer.id) {
                    answerData.id = answer.id;
                } else {
                    answerData.question_id = answer.question_id;
                }
                
                if (answer.attachment) {
                    answerData.attachment = answer.attachment;
                }
                
                if (answer.remove) {
                    answerData.remove = true;
                }
                
                return answerData;
            });
            debugger
            const updateData = {
                answers: answers,
                signoff_signature: formData.signoff_signature,
                status: this.investigationForm.valid && formData.signoff_signature != '' ? 'COMPLETE' : 'PENDING'
            };

            this.vehicleFileService.updateInvestigation(this.activityId, this.investigationId, updateData)
                .pipe(
                    takeUntil(this.destroy$),
                    finalize(() => this.isSaving = false)
                )
                .subscribe(
                    (response) => {
                        this.toasterErrorService.toasterSuccess('Investigation updated successfully');
                        this.loadInvestigationDetails(); // Reload to get updated data
                    },
                    (error) => {
                        console.error('Error updating investigation:', error);
                        this.toasterErrorService.toasterErr('Failed to update investigation');
                    }
                );
        } else {
            this.markFormGroupTouched();
            this.toasterErrorService.toasterErr('Please fill in all required fields');
        }
    }

    markFormGroupTouched() {
        Object.keys(this.investigationForm.controls).forEach(key => {
            const control = this.investigationForm.get(key);
            control.markAsTouched();
            
            if (control instanceof FormArray) {
                control.controls.forEach(arrayControl => {
                    if (arrayControl instanceof FormGroup) {
                        Object.keys(arrayControl.controls).forEach(arrayKey => {
                            arrayControl.get(arrayKey).markAsTouched();
                        });
                    }
                });
            }
        });
    }

    goBack() {
        this.router.navigate(['/vehicle-file/activity']);
    }

    getQuestionById(questionId: number) {
        if (this.investigationData && this.investigationData.template && this.investigationData.template.questions) {
            return this.investigationData.template.questions.find(q => q.id === questionId);
        }
        return null;
    }

    getQuestionTypeById(questionId: number): string {
        const question = this.getQuestionById(questionId);
        return question ? question.answer_type : 'TEXT';
    }

    formatDateForInput(dateString: string): string {
        if (!dateString) return '';
        
        try {
            // Convert ISO date string to YYYY-MM-DD format for HTML date input
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    }

    formatDateForAPI(dateString: string): string {
        if (!dateString) return '';
        
        try {
            // Convert YYYY-MM-DD format to ISO date string for API
            const date = new Date(dateString + 'T00:00:00.000Z');
            return date.toISOString();
        } catch (error) {
            console.error('Error formatting date for API:', error);
            return '';
        }
    }

    getAnswerType(questionId: number): string {
        const question = this.getQuestionById(questionId);
        return question ? question.answer_type : 'TEXT';
    }

    getAnswerChoices(questionId: number): any[] {
        const question = this.getQuestionById(questionId);
        return question ? question.answer_choices || [] : [];
    }

    isFieldInvalid(fieldName: string, answerIndex?: number): boolean {
        if (answerIndex !== undefined) {
            const answerControl = this.answersArray.at(answerIndex);
            const fieldControl = answerControl.get(fieldName);
            return fieldControl.invalid && (fieldControl.dirty || fieldControl.touched);
        } else {
            const control = this.investigationForm.get(fieldName);
            return control.invalid && (control.dirty || control.touched);
        }
    }

    getFieldError(fieldName: string, answerIndex?: number): string {
        if (answerIndex !== undefined) {
            const answerControl = this.answersArray.at(answerIndex);
            const fieldControl = answerControl.get(fieldName);
            return this.getErrorMessage(fieldControl.errors);
        } else {
            const control = this.investigationForm.get(fieldName);
            return this.getErrorMessage(control.errors);
        }
    }

    private getErrorMessage(errors: any): string {
        if (!errors) return '';
        
        if (errors.required) return 'This field is required';
        if (errors.maxlength) return `Maximum length is ${errors.maxlength.requiredLength}`;
        if (errors.minlength) return `Minimum length is ${errors.minlength.requiredLength}`;
        if (errors.email) return 'Please enter a valid email address';
        if (errors.pattern) return 'Please enter a valid format';
        
        return 'Invalid input';
    }

    /**
     * Extract dropdown value from combined answer string
     * Expected format: "Dropdown Value|Text Value" or just "Dropdown Value"
     */
    private extractDropdownFromAnswer(answer: string): string {
        if (!answer) return '';
        const parts = answer.split('|');
        return parts[0] || '';
    }

    /**
     * Extract text value from combined answer string
     * Expected format: "Dropdown Value|Text Value" or just "Dropdown Value"
     */
    private extractTextFromAnswer(answer: string): string {
        if (!answer) return '';
        const parts = answer.split('|');
        return parts.length > 1 ? parts[1] : '';
    }

    /**
     * Combine dropdown and text values into single answer string
     * Format: "Dropdown Value|Text Value"
     */
    private combineDropdownAndText(dropdownValue: string, textValue: string): string {
        if (!dropdownValue && !textValue) return '';
        if (!dropdownValue) return textValue;
        if (!textValue) return dropdownValue;
        return `${dropdownValue}|${textValue}`;
    }
}



// WEBPACK FOOTER //
// /Users/brianyoung/Documents/Development/hfg/datalive-core/gae-web/datalive/client/src/app/Vehicle-file/components/Investigation/investigation.component.ts